import { supabase } from './client';
import { withRequestDeadline } from './requestDeadline';

export const QR_SESSION_CHANGED = 'mozzoapp:qr-session-changed';
const PREFIX = 'mozzoapp:qr-session:v2:';
const EXPIRED_QUERY = 'qrAccess';
const requests = new Map();
const consumedExternalEntries = new Set();

const storage = () => window.localStorage;
const read = (qrToken) => JSON.parse(storage().getItem(`${PREFIX}${qrToken}`) || 'null');
const write = (qrToken, value) => storage().setItem(`${PREFIX}${qrToken}`, JSON.stringify(value));

export const getQrCustomerSessionToken = (qrToken) => read(qrToken)?.token || '';

const isTableEntryUrl = (url, qrToken) => {
  try {
    return decodeURIComponent(url.pathname).replace(/\/$/, '') === `/mesa/${qrToken}`;
  } catch (_) {
    return false;
  }
};

function consumeExternalTableEntry(qrToken) {
  const url = new URL(window.location.href);
  if (!isTableEntryUrl(url, qrToken) || url.searchParams.has(EXPIRED_QUERY)) return false;
  const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
  const legacyType = window.performance?.navigation?.type;
  if ((navigation && navigation.type !== 'navigate') || (!navigation && legacyType != null && legacyType !== 0)) return false;
  const entryKey = `${window.performance.timeOrigin}:${qrToken}`;
  if (consumedExternalEntries.has(entryKey)) return false;
  // Consume only this document entry, never component rerenders or retry buttons.
  consumedExternalEntries.add(entryKey);
  return true;
}

export function markQrCustomerEntryExpired(qrToken) {
  const url = new URL(window.location.href);
  if (isTableEntryUrl(url, qrToken)) {
    // Keep reload/back on the expired screen; the printed QR still has its clean URL.
    url.searchParams.set(EXPIRED_QUERY, 'expired');
    window.history.replaceState(window.history.state, '', url);
  }
}

export function closeQrCustomerSession(qrToken) {
  const current = read(qrToken);
  if (current) write(qrToken, { ...current, closed: true });
  markQrCustomerEntryExpired(qrToken);
}

export function isMatchingTableQr(value, qrToken, origin = window.location.origin) {
  try {
    const url = new URL(value);
    return url.origin === origin && decodeURIComponent(url.pathname) === `/mesa/${qrToken}`;
  } catch (_) {
    return false;
  }
}

export function resetQrCustomerSessionAfterScan(qrToken, scannedValue) {
  if (!isMatchingTableQr(scannedValue, qrToken)) {
    throw new Error('Ese codigo no corresponde a esta mesa. Escanea el QR de esta mesa.');
  }
  write(qrToken, { token: window.crypto.randomUUID(), closed: false });
}

export async function startStaffTabletQrSession(qrToken) {
  const token = window.crypto.randomUUID();
  const { data, error } = await withRequestDeadline((signal) => supabase.rpc('start_staff_tablet_qr_session', {
    p_qr_token: qrToken, p_customer_session_token: token,
  }).abortSignal(signal));
  if (error) throw error;
  if (!data?.valid) throw expiredError(data?.message);
  write(qrToken, { token, closed: false });
  return data;
}

function expiredError(message) {
  const error = new Error(message || 'Tu acceso a esta mesa vencio. Vuelve a escanear el QR desde la mesa.');
  error.code = 'QR_ACCESS_ENDED';
  return error;
}

export async function validateQrCustomerSession(qrToken, { initial = false, legacyToken = '' } = {}) {
  let record;
  let restarted = false;
  let canReenter = false;
  try {
    const url = new URL(window.location.href);
    if (initial && isTableEntryUrl(url, qrToken) && url.searchParams.has(EXPIRED_QUERY)) {
      throw expiredError();
    }
    canReenter = initial && consumeExternalTableEntry(qrToken);
    record = read(qrToken);
    if (record?.closed && canReenter) {
      record = { token: window.crypto.randomUUID(), closed: false };
      write(qrToken, record);
      restarted = true;
    }
    if (!record && initial) {
      record = { token: legacyToken || window.crypto.randomUUID(), closed: false };
      write(qrToken, record);
    }
  } catch (error) {
    if (error.code === 'QR_ACCESS_ENDED') throw error;
    throw new Error('Necesitamos guardar la sesion de esta mesa. Habilita el almacenamiento del navegador y vuelve a intentar.');
  }
  if (!record || record.closed) throw expiredError();
  const key = `${qrToken}:${record.token}`;
  if (requests.has(key)) return requests.get(key);
  const promise = (async () => {
    const requestAccess = () => withRequestDeadline(
      (signal) => supabase.rpc(initial ? 'start_public_qr_session' : 'get_public_qr_session', {
        p_qr_token: qrToken,
        p_customer_session_token: record.token,
      }).abortSignal(signal),
      { timeoutMs: 8000, message: 'No pudimos comprobar el acceso a la mesa. Revisa tu conexion y vuelve a intentar.' }
    );
    let { data, error } = await requestAccess();
    if (error) throw error;
    // Always check the old token first, so an unpaid account survives a new scan.
    if (read(qrToken)?.token !== record.token) throw expiredError();
    if (!data?.valid && canReenter && !restarted && ['expired', 'settled', 'session_closed'].includes(data?.reason)) {
      record = { token: window.crypto.randomUUID(), closed: false };
      write(qrToken, record);
      restarted = true;
      ({ data, error } = await requestAccess());
      if (error) throw error;
    }
    // Ignore a response from a session replaced by a later entry in another tab.
    if (read(qrToken)?.token !== record.token) throw expiredError();
    if (!data?.valid) {
      closeQrCustomerSession(qrToken);
      window.dispatchEvent(new CustomEvent(QR_SESSION_CHANGED, { detail: { qrToken, ...data } }));
      throw expiredError(data?.message);
    }
    const remainingMs = data.expiresAt ? Date.parse(data.expiresAt) - Date.parse(data.serverNow) : null;
    const access = {
      ...data,
      qrToken,
      restarted,
      checkAt: Number.isFinite(remainingMs) ? new Date(Date.now() + Math.max(0, remainingMs)).toISOString() : '',
    };
    window.dispatchEvent(new CustomEvent(QR_SESSION_CHANGED, { detail: access }));
    return access;
  })();
  requests.set(key, promise);
  try {
    return await promise;
  } finally {
    requests.delete(key);
  }
}
